import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const api = axios.create({ baseURL: API, withCredentials: true });

export { api };

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null); // null = checking, false = anonymous, object = logged
  const [booting, setBooting] = useState(true);

  const fetchMe = useCallback(async () => {
    // If returning from Google OAuth, skip auto /auth/me: the callback page
    // will exchange the session_id and refresh state afterwards.
    if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
      setBooting(false);
      return;
    }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      setUser(false);
    } finally {
      setBooting(false);
    }
  }, []);

  const loginWithGoogleSession = useCallback(async (sessionId) => {
    const { data } = await api.post("/auth/google-session", { session_id: sessionId });
    setUser(data);
    return data;
  }, []);

  useEffect(() => {
    fetchMe();
  }, [fetchMe]);

  const login = useCallback(async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    setUser(data);
    return data;
  }, []);

  const register = useCallback(async (name, email, password) => {
    const { data } = await api.post("/auth/register", { name, email, password });
    setUser(data);
    return data;
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch {
      /* logout is best-effort; ignore network errors */
    }
    setUser(false);
  }, []);

  const value = useMemo(
    () => ({ user, booting, login, register, logout, refresh: fetchMe, loginWithGoogleSession }),
    [user, booting, login, register, logout, fetchMe, loginWithGoogleSession]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be within AuthProvider");
  return ctx;
};

export const formatApiError = (detail) => {
  if (detail == null) return "Something went wrong.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
};
